import {
  commentsQueryParams,
  err,
  ok,
  parseCommentPage,
  resolveInstanceUrl,
  withQuery,
  type AbortLike,
  type CommentPage,
  type CommentQuery,
  type Result,
} from '@visious/core';
import type { Platform } from '../platform/instance';
import { fetchJson } from './json';
import type { ApiProblem } from './videos';

/**
 * `GET /api/v1/comments/{videoId}` — the top-level list *and* every replies page, since the two
 * are the same endpoint with a different token (§6). One function, therefore, rather than two that
 * would have to be kept in step.
 *
 * The parameters come from core (`commentsQueryParams`) rather than being written here, because
 * both of them are traps: the name is `sort_by` and not `sort`, and a continuation must carry no
 * sort at all.
 */
export async function fetchCommentPage(
  platform: Platform,
  query: CommentQuery,
  signal?: AbortLike,
): Promise<Result<CommentPage, ApiProblem>> {
  const path = `/api/v1/comments/${encodeURIComponent(query.videoId)}`;
  const base = resolveInstanceUrl(platform.config, path);
  if (base === null) return err({ kind: 'bad-url', path });

  const url = withQuery(base, commentsQueryParams(query));
  if (url === null) return err({ kind: 'bad-url', path });

  // `api/json.ts` since 4a — the zero-byte `200` reads as itself rather than as a proxy answering.
  const raw = await fetchJson(platform, url, signal);
  if (!raw.ok) return err(raw.error);

  const parsed = parseCommentPage(raw.value, platform.parseContext);
  if (!parsed.ok) return err({ kind: 'parse', error: parsed.error });

  return ok(parsed.value);
}
